import React, { FormEvent, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Navigate } from 'react-router';
import {
  apiAudience,
  apiPathV4,
  auth0ClientId,
  auth0Domain,
  cognitoClientId,
  cognitoDomain,
  hasCognitoLane,
  isLocalEnvironment,
} from '../config';
import { useAppAuth } from '../contexts';
import logo from '../images/logo-blue.svg';
import { isAppLoginPath } from '../utils/appLogin';
import {
  buildCognitoAuthorizeUrl,
  completeCognitoLogin,
  isCognitoCallback,
  redirectAfterCognitoLogin,
  redirectToCognito,
  takeCognitoReturnTo,
} from '../utils/cognitoAuth';
import {
  controlIssuerForHost,
  controlLoginPath,
  forgetMcpControlLogin,
  peekMcpControlLogin,
  postMcpControlSession,
  readMcpControlLogin,
  rememberMcpControlLogin,
} from '../utils/mcpControlLogin';
import { buildMcpIdpUrl, goToIdp, readMcpAuthorizeRequest } from '../utils/mcpOAuthHandoff';
import { claimRealmRedirect, clearRealmHint, hasTriedRealmRedirect } from '../utils/realmHint';
import './Login.css';

type IdpLookupResponse = {
  provider: 'auth0' | 'cognito';
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function _normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function _savedReturnTo(): string | undefined {
  const saved = localStorage.getItem('redirectPath');
  if (!saved || isAppLoginPath(saved)) {
    return undefined;
  }
  return saved;
}

/**
 * Ask the backend which provider owns this email domain.
 *
 * Fails open to Auth0: the endpoint is advisory, so a lookup outage must not
 * block sign-in.
 *
 * Example: `pat@ept.ai` → `'auth0'`; endpoint down → `'auth0'`.
 */
async function _lookupProvider(email: string): Promise<IdpLookupResponse['provider']> {
  try {
    const url = `${apiPathV4()}/auth/idp?email=${encodeURIComponent(email)}`;
    const response = await fetch(url);
    if (!response.ok) {
      return 'auth0';
    }
    const payload = (await response.json()) as IdpLookupResponse;
    return payload.provider === 'cognito' ? 'cognito' : 'auth0';
  } catch {
    return 'auth0';
  }
}

export default function Login(): JSX.Element {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    adoptCognitoSession,
    getIdTokenClaims,
  } = useAppAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Cognito sends the user back here with ?code=. Exchanging it is the only
  // work this page does on mount, so hold the form until it resolves.
  const [exchanging, setExchanging] = useState(() => isCognitoCallback());
  // Returning SSO users skip the form and go straight to their IdP (EPD-79).
  const [redirecting, setRedirecting] = useState(false);
  const realmRedirectChecked = useRef(false);

  useEffect(() => {
    if (!isCognitoCallback()) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const tokens = await completeCognitoLogin();
        if (cancelled) return;
        const pending = peekMcpControlLogin();
        const returnTo = pending ? controlLoginPath(pending) : takeCognitoReturnTo() || '/';
        adoptCognitoSession(tokens);
        // A real navigation both restores the deep link and drops the spent
        // code. history.replaceState alone would be overwritten by the
        // authenticated <Navigate> branch below.
        redirectAfterCognitoLogin(returnTo);
      } catch (exchangeError) {
        if (cancelled) return;
        setError(
          exchangeError instanceof Error
            ? exchangeError.message
            : 'Sign-in could not be completed. Please try again.'
        );
      } finally {
        if (!cancelled) setExchanging(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adoptCognitoSession]);

  /**
   * Everything the form does after it has a valid email: pick the provider,
   * then redirect. Returns a message to show when sign-in cannot start here.
   * Also used for a returning username-password user's saved email (EPD-79).
   */
  const signInWithEmail = useCallback(
    async (address: string): Promise<string | undefined> => {
      const pending = readMcpControlLogin(
        window.location.search,
        controlIssuerForHost(window.location.hostname)
      );
      const returnTo = pending
        ? `/login?mcp_txn=${encodeURIComponent(pending.txn)}&iss=${encodeURIComponent(pending.issuer)}`
        : _savedReturnTo();
      if (isLocalEnvironment()) {
        await loginWithRedirect({
          appState: returnTo ? { returnTo } : undefined,
          authorizationParams: { redirect_uri: window.location.origin },
        });
        return undefined;
      }

      const mcpSearch = window.location.search;
      const provider = await _lookupProvider(address);
      if (readMcpAuthorizeRequest(mcpSearch)) {
        const idpUrl = buildMcpIdpUrl(provider, address, mcpSearch, {
          auth0Domain: auth0Domain(),
          auth0ClientId: auth0ClientId(),
          audience: apiAudience(),
          cognitoDomain: cognitoDomain(),
          cognitoClientId: cognitoClientId(),
          origin: window.location.origin,
        });
        if (!idpUrl) {
          return 'Single sign-on for this domain is not available yet. Please contact support.';
        }
        // Stay in this window. ChatGPT reads the code only in the window it opened.
        goToIdp(idpUrl);
        return undefined;
      }
      if (provider === 'cognito') {
        // Cognito resolves idp_identifier server-side and redirects straight to
        // the customer's IdP, so the user is never asked for their email again.
        const authorizeUrl = hasCognitoLane()
          ? await buildCognitoAuthorizeUrl(address, returnTo)
          : null;
        if (!authorizeUrl) {
          // The backend can route a domain to Cognito in an environment that
          // has no pool. Say so rather than sending the user to Auth0, where
          // their account does not exist.
          return 'Single sign-on for this domain is not available yet. Please contact support.';
        }
        redirectToCognito(authorizeUrl);
        return undefined;
      }

      // No `connection`: Auth0 does home-realm discovery from login_hint, the
      // same way it does when the email is typed on login.ept.ai today.
      await loginWithRedirect({
        appState: returnTo ? { returnTo } : undefined,
        authorizationParams: {
          redirect_uri: window.location.origin,
          login_hint: address,
        },
      });
      return undefined;
    },
    [loginWithRedirect]
  );

  useEffect(() => {
    // An MCP client is waiting for its own code. Do not send this browser
    // through the saved app login, which would drop the client's PKCE.
    if (
      readMcpAuthorizeRequest(window.location.search) ||
      readMcpControlLogin(window.location.search, controlIssuerForHost(window.location.hostname))
    ) {
      return;
    }
    // Wait for the SDK: a user with a live session is sent into the app below.
    if (realmRedirectChecked.current || isLoading || isAuthenticated) {
      return;
    }
    if (isCognitoCallback() || isLocalEnvironment()) {
      return;
    }
    // Once per mount: a second run would read its own attempt as a failure.
    realmRedirectChecked.current = true;

    // Still signed out after this tab's one attempt: the saved tenant is
    // stale, the user was removed, the IdP sent back ?error=, or they came
    // Back from the IdP. Forget it so the email form is the way forward.
    if (hasTriedRealmRedirect()) {
      clearRealmHint();
      return;
    }
    // An IdP error from the form path: show the form, keep the saved tenant.
    if (new URLSearchParams(window.location.search).has('error')) {
      return;
    }

    const hint = claimRealmRedirect();
    if (!hint) {
      return;
    }

    setRedirecting(true);
    void (async () => {
      if (hint.lane === 'password') {
        // Same path as typing the saved email into the form.
        const message = await signInWithEmail(hint.email);
        if (message) {
          throw new Error(message);
        }
        return;
      }
      const returnTo = _savedReturnTo();
      if (hint.lane === 'cognito') {
        const authorizeUrl = hasCognitoLane()
          ? await buildCognitoAuthorizeUrl({ identityProvider: hint.connection }, returnTo)
          : null;
        if (!authorizeUrl) {
          throw new Error('No Cognito pool in this environment');
        }
        redirectToCognito(authorizeUrl);
        return;
      }
      await loginWithRedirect({
        appState: returnTo ? { returnTo } : undefined,
        authorizationParams: {
          redirect_uri: window.location.origin,
          connection: hint.connection,
        },
      });
    })().catch(() => {
      clearRealmHint();
      setRedirecting(false);
    });
  }, [isLoading, isAuthenticated, loginWithRedirect, signInWithEmail]);

  const mcpLogin = readMcpControlLogin(
    window.location.search,
    controlIssuerForHost(window.location.hostname)
  );
  useEffect(() => {
    if (mcpLogin) {
      rememberMcpControlLogin(mcpLogin);
    }
  }, [mcpLogin]);
  const controlReady = Boolean(mcpLogin) && !isLoading && isAuthenticated && !isCognitoCallback();

  const finishControlLogin = async (): Promise<void> => {
    if (!mcpLogin) {
      return;
    }
    setError(null);
    setSubmitting(true);
    setRedirecting(true);
    try {
      const claims = await getIdTokenClaims();
      const idToken = claims?.__raw;
      if (!idToken) {
        throw new Error('Could not finish connecting.');
      }
      const finishUrl = await postMcpControlSession(mcpLogin.issuer, mcpLogin.txn, idToken);
      forgetMcpControlLogin();
      window.location.assign(finishUrl);
    } catch (completeError) {
      setSubmitting(false);
      setRedirecting(false);
      setError(
        completeError instanceof Error ? completeError.message : 'Could not finish connecting.'
      );
    }
  };

  const rememberedLogin = peekMcpControlLogin();
  if (
    !isLoading &&
    isAuthenticated &&
    !isCognitoCallback() &&
    !readMcpAuthorizeRequest(window.location.search) &&
    !mcpLogin
  ) {
    if (rememberedLogin) {
      return <Navigate to={controlLoginPath(rememberedLogin)} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const signedInMcpEmail =
    !isLoading &&
    isAuthenticated &&
    !isCognitoCallback() &&
    readMcpAuthorizeRequest(window.location.search)
      ? _normalizeEmail(user?.email ?? '')
      : '';
  const continueAsEmail = controlReady ? _normalizeEmail(user?.email ?? '') : signedInMcpEmail;
  const continueLabel = continueAsEmail
    ? `Continue as ${continueAsEmail}`
    : controlReady
      ? 'Connect this account'
      : '';

  const continueSignedIn = async (): Promise<void> => {
    if (!EMAIL_PATTERN.test(signedInMcpEmail)) {
      setError('Enter a valid work email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    setRedirecting(true);
    try {
      const message = await signInWithEmail(signedInMcpEmail);
      if (message) {
        setSubmitting(false);
        setRedirecting(false);
        setError(message);
      }
    } catch (continueError) {
      setSubmitting(false);
      setRedirecting(false);
      setError(
        continueError instanceof Error
          ? continueError.message
          : 'Unable to start sign-in. Please try again.'
      );
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const normalizedEmail = _normalizeEmail(email);
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError('Enter a valid work email.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const message = await signInWithEmail(normalizedEmail);
      if (message) {
        setSubmitting(false);
        setError(message);
      }
    } catch (submitError) {
      setSubmitting(false);
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to start sign-in. Please try again.'
      );
    }
  };

  if (redirecting) {
    return (
      <div className="login-page">
        <div className="login-page__card">
          <img className="login-page__logo" src={logo} alt="ept AI" />
          <h1 className="login-page__title">Signing you in…</h1>
          <p className="login-page__subtitle" role="status">
            Taking you to your company&apos;s sign-in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-page__card">
        <img className="login-page__logo" src={logo} alt="ept AI" />
        <h1 className="login-page__title">Sign in</h1>
        <p className="login-page__subtitle">Enter your work email to continue.</p>
        <form className="login-page__form" onSubmit={handleSubmit}>
          {continueLabel ? (
            <button
              className="login-page__submit"
              type="button"
              disabled={submitting || exchanging || isLoading}
              onClick={() => {
                void (controlReady ? finishControlLogin() : continueSignedIn());
              }}
            >
              {continueLabel}
            </button>
          ) : null}
          <label className="login-page__label" htmlFor="login-email">
            Work email
          </label>
          <input
            id="login-email"
            className="login-page__input"
            type="email"
            placeholder="Work email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            disabled={submitting || exchanging || isLoading}
            onChange={event => setEmail(event.target.value)}
          />
          {error ? (
            <p className="login-page__error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="login-page__submit"
            type="submit"
            disabled={submitting || exchanging || isLoading}
          >
            {submitting || exchanging ? 'Continuing…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
